import React from 'react';
import {
  Text,
  Badge,
  tokens,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import { GradeData } from '../models/types';

const useStyles = makeStyles({
  badgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    opacity: 0.85,
    transition: 'opacity 0.2s ease, background-color 0.2s ease',
    cursor: 'help',
    ':hover': {
      opacity: 1,
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  tooltipContent: {
    padding: tokens.spacingVerticalM,
    minWidth: '400px',
    width: '600px',
    maxWidth: '700px',
    maxHeight: '400px',
    overflowY: 'auto',
    // Try to force the content to maintain its size
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  customTooltip: {
    // Try to override Fluent UI's tooltip width constraints
    minWidth: '400px !important',
    width: '600px !important',
    maxWidth: '700px !important',
  },
  tooltipHeader: {
    marginBottom: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tooltipReasoning: {
    lineHeight: '1.4',
  },
});

interface InlineGradeBadgeProps {
  grade: GradeData;
}

export const InlineGradeBadge: React.FC<InlineGradeBadgeProps> = ({ grade }) => {
  const classes = useStyles();

  const tooltipContent = (
    <div className={classes.tooltipContent}>
      <div className={classes.tooltipHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalXS }}>
          <Text weight="semibold" size={300}>
            Quality Score:
          </Text>
          <Badge 
            appearance={grade.score >= 4 ? 'filled' : grade.score >= 3 ? 'outline' : 'tint'}
            color={grade.score >= 4 ? 'success' : grade.score >= 3 ? 'warning' : 'danger'}
          >
            {grade.score}/5
          </Badge>
        </div>
        {grade.evaluator && (
          <Text size={200}>
            Evaluator: {grade.evaluator}
          </Text>
        )}
      </div>
      
      <div>
        <Text weight="semibold" size={300} style={{ marginBottom: tokens.spacingVerticalXS }}>
          Evaluation Reasoning:
        </Text>
        <br />
        <Text size={200} className={classes.tooltipReasoning}>
          {grade.reasoning}
        </Text>
      </div>
      <br />
        <Text size={200}>
            {new Date(grade.timestamp * 1000).toLocaleString()}
        </Text>
    </div>
  );

  return (
    <div style={{ 
      // Try to create a containing context that might influence tooltip sizing
      position: 'relative',
      width: '100%'
    }}>
      <Tooltip
        content={tooltipContent}
        relationship="description"
        positioning="above-start"
        appearance="inverted"
      >
        <div className={classes.badgeContainer}>
        <Badge 
          appearance={grade.score >= 4 ? 'filled' : grade.score >= 3 ? 'outline' : 'tint'}
          color={grade.score >= 4 ? 'success' : grade.score >= 3 ? 'warning' : 'danger'}
          size="small" style={{ minWidth: 'fit-content' }}
        >
          Groundedness: {grade.score}/5
        </Badge>
        <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
          Hover for details - This is a general guideline - AI-generated content may still be incorrect
        </Text>
      </div>
    </Tooltip>
    </div>
  );
};

export default InlineGradeBadge;